import * as THREE from "three";

import {
  ARENA_GUARDIAN_LABEL,
  CENTURION_BANNER,
  DREAD_HARVESTER_BANNER,
  FOUNDRY_LEVIATHAN_BANNER,
  NULL_CROWN_BANNER,
  SIEGE_WARDEN_BANNER,
  VELOCITY_SOVEREIGN_BANNER,
  poiDisplayName,
  type PoiKind,
} from "../content/pois";
import type { WorldSnapshot } from "../simulation/snapshot";
import { sampleHeight } from "../world/terrain";

const LABEL_RANGE_M = 96;
const SAFE_MARGIN_PX = 12;
const LABEL_GAP_PX = 8;
const PROJECT = new THREE.Vector3();

export type ScreenRect = { left: number; top: number; right: number; bottom: number };
export type LabelCandidate = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  priority: number;
};
export type LabelPlacement = LabelCandidate & {
  placedX: number;
  placedY: number;
  hidden: boolean;
};

type RenderCandidate = LabelCandidate & { kind: string; text: string; near: string };
type LabelSlot = {
  el: HTMLElement;
  leader: HTMLElement;
  kind: string;
  text: string;
  near: string;
};

/** Priority-ordered, deterministic screen-space collision layout. */
export function layoutWorldLabels(
  candidates: readonly LabelCandidate[],
  safe: ScreenRect,
  exclusions: readonly ScreenRect[],
): LabelPlacement[] {
  const placedRects: ScreenRect[] = [];
  const result = new Map<string, LabelPlacement>();
  const vertical = [0, -22, 22, -44, 44, -66, 66];
  const horizontal = [-24, 24, -48, 48, -72, 72];
  const ordered = [...candidates].sort(
    (a, b) => b.priority - a.priority || a.id.localeCompare(b.id),
  );

  for (const candidate of ordered) {
    let accepted: { x: number; y: number; rect: ScreenRect } | null = null;
    const attempts = [
      ...vertical.map((dy) => ({ dx: 0, dy })),
      ...horizontal.map((dx) => ({ dx, dy: 0 })),
    ];
    for (const offset of attempts) {
      const x = clamp(
        candidate.x + offset.dx,
        safe.left + candidate.width / 2,
        safe.right - candidate.width / 2,
      );
      const y = clamp(candidate.y + offset.dy, safe.top + candidate.height, safe.bottom);
      const rect = labelRect(x, y, candidate.width, candidate.height);
      if (exclusions.some((other) => overlaps(rect, other, LABEL_GAP_PX))) continue;
      if (placedRects.some((other) => overlaps(rect, other, LABEL_GAP_PX))) continue;
      accepted = { x, y, rect };
      break;
    }
    if (accepted) placedRects.push(accepted.rect);
    result.set(candidate.id, {
      ...candidate,
      placedX: accepted?.x ?? candidate.x,
      placedY: accepted?.y ?? candidate.y,
      hidden: accepted == null,
    });
  }
  return candidates.map((candidate) => result.get(candidate.id)!);
}

/** Screen-space nameplates for POIs, priority targets, and navigation. */
export class WorldLabelLayer {
  private readonly layer: HTMLElement;
  private readonly slots: LabelSlot[] = [];
  private canvasW = 0;
  private canvasH = 0;
  private hostRect: DOMRect | null = null;
  private rectFrame = -1;
  private frame = 0;

  public constructor(private readonly host: HTMLElement) {
    this.layer = document.createElement("div");
    this.layer.className = "world-labels";
    this.layer.setAttribute("aria-hidden", "true");
    host.appendChild(this.layer);
  }

  public render(
    snap: WorldSnapshot,
    camera: THREE.PerspectiveCamera,
    canvas: HTMLCanvasElement,
  ): void {
    this.frame += 1;
    if (this.canvasW === 0 || this.canvasH === 0 || this.frame - this.rectFrame >= 8) {
      this.hostRect = canvas.getBoundingClientRect();
      this.canvasW = this.hostRect.width;
      this.canvasH = this.hostRect.height;
      this.rectFrame = this.frame;
    }
    const width = this.canvasW;
    const height = this.canvasH;
    if (width === 0 || height === 0) {
      this.hideFrom(0);
      return;
    }

    const candidates: RenderCandidate[] = [];
    const px = snap.player.x;
    const py = snap.player.y;
    const carrierDist = Math.hypot(snap.carrierX - px, snap.carrierY - py);
    if (carrierDist < LABEL_RANGE_M + 40 || snap.carrierNav?.active) {
      const position = projectWorld(
        snap.carrierX,
        sampleHeight(snap.seed, snap.carrierX, snap.carrierY) + 16,
        snap.carrierY,
        camera,
        width,
        height,
      );
      if (position) {
        candidates.push(
          makeCandidate(
            "carrier",
            "carrier",
            "CARRIER",
            "",
            position,
            snap.carrierNav?.active ? 95 : 70,
          ),
        );
      }
    }

    for (const poi of snap.pois) {
      if (poi.cleared) continue;
      const distance = Math.hypot(poi.x - px, poi.y - py);
      if (distance > LABEL_RANGE_M) continue;
      const position = projectWorld(
        poi.x,
        sampleHeight(snap.seed, poi.x, poi.y) + 5.2,
        poi.y,
        camera,
        width,
        height,
      );
      if (!position) continue;
      const containing = distance <= poi.radius;
      const near = distance <= poi.radius + 8;
      const name = poiDisplayName(poi.kind as PoiKind);
      candidates.push(
        makeCandidate(
          `poi-${poi.id}`,
          poi.kind,
          name,
          near ? "1" : "0",
          position,
          containing ? 80 : near ? 60 : 40,
        ),
      );
    }

    for (const enemy of snap.enemies) {
      if (enemy.hull <= 0) continue;
      const bounty = snap.bounty?.targetId === enemy.id;
      const stolenSalvage = enemy.stolenSalvage ?? 0;
      if (!enemy.isArenaGuardian && !bounty && stolenSalvage <= 0) continue;
      if (Math.hypot(enemy.x - px, enemy.y - py) > LABEL_RANGE_M + 24) continue;
      const position = projectWorld(enemy.x, enemy.altitude + 2.4, enemy.y, camera, width, height);
      if (!position) continue;
      if (enemy.isArenaGuardian) {
        candidates.push(
          makeCandidate(
            `guardian-${enemy.id}`,
            "guardian",
            enemy.role === "harvester"
              ? DREAD_HARVESTER_BANNER
              : enemy.role === "centurion"
                ? CENTURION_BANNER
                : enemy.role === "null_crown"
                  ? NULL_CROWN_BANNER
                  : enemy.role === "leviathan"
                    ? FOUNDRY_LEVIATHAN_BANNER
                    : enemy.role === "sovereign"
                      ? VELOCITY_SOVEREIGN_BANNER
                      : enemy.role === "warden"
                        ? SIEGE_WARDEN_BANNER
                        : ARENA_GUARDIAN_LABEL,
            "1",
            position,
            110,
          ),
        );
      }
      if (bounty) {
        candidates.push(
          makeCandidate(
            `bounty-${enemy.id}`,
            "bounty",
            "HUNT",
            "1",
            { x: position.x, y: position.y + 16 },
            105,
          ),
        );
      }
      if (stolenSalvage > 0) {
        candidates.push(
          makeCandidate(
            `scavenger-${enemy.id}`,
            "scavenger",
            `STOLEN SCRAP ${stolenSalvage}`,
            "1",
            { x: position.x, y: position.y + (bounty ? 32 : 16) },
            100,
          ),
        );
      }
    }

    const safe = {
      left: SAFE_MARGIN_PX,
      top: SAFE_MARGIN_PX,
      right: width - SAFE_MARGIN_PX,
      bottom: height - SAFE_MARGIN_PX,
    };
    const exclusions = this.hudExclusions();
    const layout = layoutWorldLabels(candidates, safe, exclusions);
    let used = 0;
    for (let index = 0; index < candidates.length; index += 1) {
      const candidate = candidates[index]!;
      const placement = layout[index]!;
      if (placement.hidden) continue;
      this.place(used, candidate, placement);
      used += 1;
    }
    this.hideFrom(used);
  }

  public dispose(): void {
    this.layer.remove();
    this.slots.length = 0;
  }

  private hudExclusions(): ScreenRect[] {
    if (!this.hostRect) return [];
    const host = this.hostRect;
    const rects: ScreenRect[] = [];
    for (const element of this.host.querySelectorAll<HTMLElement>(".combat-hud-region")) {
      if (element.offsetParent == null) continue;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) continue;
      rects.push({
        left: rect.left - host.left,
        top: rect.top - host.top,
        right: rect.right - host.left,
        bottom: rect.bottom - host.top,
      });
    }
    return rects;
  }

  private place(index: number, candidate: RenderCandidate, placement: LabelPlacement): void {
    let slot = this.slots[index];
    if (!slot) {
      const leader = document.createElement("i");
      leader.className = "world-label-leader";
      const el = document.createElement("span");
      this.layer.append(leader, el);
      slot = { el, leader, kind: "", text: "", near: "" };
      this.slots[index] = slot;
    }
    const { el, leader } = slot;
    el.hidden = false;
    if (slot.kind !== candidate.kind) {
      el.className = classForKind(candidate.kind);
      if (candidate.kind === "carrier" || candidate.kind === "guardian") delete el.dataset.kind;
      else el.dataset.kind = candidate.kind;
      slot.kind = candidate.kind;
    }
    if (slot.text !== candidate.text) {
      el.textContent = candidate.text;
      slot.text = candidate.text;
    }
    if (candidate.kind !== "carrier" && slot.near !== candidate.near) {
      el.dataset.near = candidate.near;
      slot.near = candidate.near;
    }
    el.style.left = `${placement.placedX}px`;
    el.style.top = `${placement.placedY}px`;
    const dx = placement.placedX - candidate.x;
    const dy = placement.placedY - candidate.y;
    const distance = Math.hypot(dx, dy);
    leader.hidden = distance <= 16;
    if (distance > 16) {
      leader.style.left = `${candidate.x}px`;
      leader.style.top = `${candidate.y}px`;
      leader.style.width = `${distance}px`;
      leader.style.transform = `rotate(${Math.atan2(dy, dx)}rad)`;
    }
  }

  private hideFrom(start: number): void {
    for (let i = start; i < this.slots.length; i += 1) {
      this.slots[i]!.el.hidden = true;
      this.slots[i]!.leader.hidden = true;
    }
  }
}

function makeCandidate(
  id: string,
  kind: string,
  text: string,
  near: string,
  position: { x: number; y: number },
  priority: number,
): RenderCandidate {
  return {
    id,
    kind,
    text,
    near,
    x: position.x,
    y: position.y,
    width: Math.max(42, text.length * 7 + 12),
    height: 20,
    priority,
  };
}

function labelRect(x: number, y: number, width: number, height: number): ScreenRect {
  return { left: x - width / 2, right: x + width / 2, top: y - height, bottom: y };
}

function overlaps(a: ScreenRect, b: ScreenRect, gap: number): boolean {
  return !(
    a.right + gap <= b.left ||
    a.left >= b.right + gap ||
    a.bottom + gap <= b.top ||
    a.top >= b.bottom + gap
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function classForKind(kind: string): string {
  if (kind === "carrier") return "world-label world-label-carrier";
  if (kind === "guardian") return "world-label world-label-guardian";
  if (kind === "bounty") return "world-label world-label-bounty";
  if (kind === "scavenger") return "world-label world-label-scavenger";
  return "world-label world-label-poi";
}

function projectWorld(
  x: number,
  worldY: number,
  y: number,
  camera: THREE.PerspectiveCamera,
  width: number,
  height: number,
): { x: number; y: number } | null {
  PROJECT.set(x, worldY, y).project(camera);
  if (PROJECT.z > 1 || PROJECT.z < -1) return null;
  const sx = ((PROJECT.x + 1) / 2) * width;
  const sy = ((1 - PROJECT.y) / 2) * height;
  if (sx < -40 || sy < -40 || sx > width + 40 || sy > height + 40) return null;
  return { x: sx, y: sy };
}
